import {
  DataGrid,
  ErrorMessage,
  GridColDef,
  LoadingSpinner,
  MediaCardItem,
  MediaListItem,
  Tabs,
} from "@msys/ui";
import {
  Button,
  Chip,
  Grid,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { PimProductPreferenceScope } from "../../../clients/graphqlTypes.js";
import { useUserData } from "../../auth/useUserData.js";
import { useCardsNumber } from "../../commons/hooks/useCardsNumber.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../commons/hooks/useCollectionView.js";
import {
  PAGE_LIST_RESULTS_PER_PAGE,
  RESULTS_PER_PAGE_OPTIONS,
} from "../../constants.js";
import {
  BrandAvailabilityScope,
  BrandLineAvailabilityScope,
  BrandPreferenceOption,
  lineName,
  useAvailableBrandOptions,
} from "../../features/products/brandPreferenceOptions.js";
import { PreferenceListItemFragment } from "../../features/products/BrandPreferences.generated.js";
import { AddPreferenceButton } from "../../features/products/buttons/AddPreferenceButton.js";
import { PreferenceSearchButton } from "../../features/products/buttons/PreferenceSearchButton.js";
import { BrandPreferenceItemActions } from "../../features/products/buttons/BrandPreferenceItemActions.js";
import { PreferenceMoveButton } from "../../features/products/buttons/PreferenceMoveButton.js";
import {
  NotRankingChip,
  PreferenceItemTags,
} from "../../features/products/components/PreferenceItemTags.js";
import { PreferenceScopeToggle } from "../../features/products/components/PreferenceScopeToggle.js";
import {
  ProductCardItem,
  ProductListItem,
} from "../../features/products/components/ProductItem.js";
import { useGetProductTo } from "../../features/products/helper.js";
import { PreferenceScopeModal } from "../../features/products/modals/PreferenceScopeModal.js";
import { ProductSearchItem__ProductSearchResultFragment } from "../../features/products/Product.generated.js";
import {
  articleNumberIdentifierKey,
  brandLineSummary,
  buildMoveHead,
  moveTargetIndex,
  positionPreferenceItems,
  PositionedPreferenceItem,
  PreferenceGroup,
  PreferenceKind,
  preferenceKindOf,
  PreferenceMove,
} from "../../features/products/productPreferences.js";
import { UseBrandPreferences } from "../../features/products/useBrandPreferences.js";
import {
  PreferenceTarget,
  usePreferenceList,
} from "../../features/products/usePreferenceList.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import { GLOBAL_PRODUCT_PREFERENCES_PATH } from "./productPreferencesPaths.js";
import {
  toUnresolvedProductSearchResult,
  usePreferredProducts,
} from "./usePreferredProducts.js";

const ALLOWED_VIEWS: CollectionView[] = ["gallery", "list", "table"];

type ArticleItem = Extract<
  PreferenceListItemFragment,
  { __typename: "PimArticlePreferenceItem" }
>;
type BrandItem = Extract<
  PreferenceListItemFragment,
  { __typename: "PimBrandPreferenceItem" }
>;
type Positioned = PositionedPreferenceItem<PreferenceListItemFragment>;

/** An article row's product, carrying the list item it stands for. */
type RankedArticleProduct = ProductSearchItem__ProductSearchResultFragment & {
  ranked: PositionedPreferenceItem<ArticleItem>;
};

interface Props {
  /** What the list reads and every write addresses. */
  target: PreferenceTarget;
  /** The product type articles are preferred for, as stored; `null` for the Global entry. */
  articleProductType: {
    productTypeKey: string;
    productTypeSystemKey: string;
  } | null;
  /** The heading of the detail, left of the view switch and the add action. */
  title: React.ReactNode;
  /** Shown between the heading and the list, e.g. why the preferences rank nothing. */
  notice?: React.ReactNode;
  /** Opens the catalogue assignment search; `null` where articles cannot be added. */
  onAssignProducts: (() => void) | null;
  /** The search these preferences rank, `productTypeId: null` for the global one; `null` where they rank nothing. */
  search: { productTypeId: string | null } | null;
  /** The articles / brands sub-tabs, limited to the kinds the target can hold. */
  tabs: {
    kind: PreferenceKind;
    kinds: readonly PreferenceKind[];
    onChange: (kind: PreferenceKind) => void;
  };
  /** Whose preferences the list shows: the caller's own or their organisation's. */
  scope: {
    value: PimProductPreferenceScope;
    onChange: (scope: PimProductPreferenceScope) => void;
  };
  /** The action of an article item, rendered beside its move menu. */
  renderArticleAction: (
    product: ProductSearchItem__ProductSearchResultFragment,
    item: ArticleItem
  ) => React.ReactNode;
  /** Where brands to add must be available; `null` offers no adding. */
  brandAvailableIn: BrandAvailabilityScope | null;
  /** Where lines offered by "Edit lines" must be available; `null` offers no line edit. */
  brandLinesAvailableIn: BrandLineAvailabilityScope | null;
  /** Under a superseded product type system: removal only, no move and no line edit. */
  inert: boolean;
  brandPreferences: UseBrandPreferences;
}

/**
 * One target's preferences as one list in search rank order — the user's articles, the
 * organisation's articles, the user's brands, the organisation's brands — in the gallery,
 * list and table views. Pages are applied to the whole list; only the visible page's articles
 * are fetched from the catalogue.
 */
export function RankedPreferenceList({
  target,
  articleProductType,
  title,
  notice,
  onAssignProducts,
  search,
  tabs,
  scope,
  renderArticleAction,
  brandAvailableIn,
  brandLinesAvailableIn,
  inert,
  brandPreferences,
}: Props) {
  const { t } = useTranslate(["Product", "Global"]);
  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      "product-preferences",
      "gallery",
      "list"
    );
  const { hasOrganisationPermission } = useUserData();
  const canWriteOrganisationScope = hasOrganisationPermission(
    "pim_catalogue_update"
  );
  const mayWrite = React.useCallback(
    (scope: PimProductPreferenceScope) =>
      scope === "USER" || canWriteOrganisationScope,
    [canWriteOrganisationScope]
  );

  const list = usePreferenceList(target);
  const globalBrandCount = brandPreferences.brandPreferences.filter(
    entry => !entry.productType
  ).length;
  // Positions are counted over the whole list, so they stay the same whichever tab shows them.
  const allPositioned = React.useMemo(
    () => positionPreferenceItems(list.items),
    [list.items]
  );
  const shownKind = tabs.kind;
  const shownScope = scope.value;
  const matchesKind = (ranked: Positioned) =>
    preferenceKindOf(ranked.group) === shownKind;
  // A tab counts its kind at both scopes; the scope toggle splits the shown tab's count, so
  // its two numbers always add up to the tab's.
  const kindCounts = {
    ARTICLES: allPositioned.filter(
      ranked => preferenceKindOf(ranked.group) === "ARTICLES"
    ).length,
    BRANDS: allPositioned.filter(
      ranked => preferenceKindOf(ranked.group) === "BRANDS"
    ).length,
  };
  const scopeCounts = {
    USER: allPositioned.filter(
      ranked => matchesKind(ranked) && ranked.item.scope === "USER"
    ).length,
    ORGANISATION: allPositioned.filter(
      ranked => matchesKind(ranked) && ranked.item.scope === "ORGANISATION"
    ).length,
  };
  const positioned = React.useMemo(
    () =>
      allPositioned.filter(
        ranked =>
          ranked.item.scope === shownScope &&
          preferenceKindOf(ranked.group) === shownKind
      ),
    [allPositioned, shownKind, shownScope]
  );

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: PAGE_LIST_RESULTS_PER_PAGE,
  });
  React.useEffect(() => {
    setPaginationModel(current =>
      current.page === 0 ? current : { ...current, page: 0 }
    );
  }, [shownKind, shownScope]);
  // A removal can shorten the list under the current page index; the view stays on the last
  // page that still has items rather than on an empty one with no pagination to leave it by.
  const lastPage = Math.max(
    0,
    Math.ceil(positioned.length / paginationModel.pageSize) - 1
  );
  const page = Math.min(paginationModel.page, lastPage);
  const clampedPaginationModel =
    page === paginationModel.page
      ? paginationModel
      : { ...paginationModel, page };

  const pageItems = React.useMemo(
    () =>
      positioned.slice(
        page * paginationModel.pageSize,
        (page + 1) * paginationModel.pageSize
      ),
    [positioned, page, paginationModel.pageSize]
  );

  const pageArticles = React.useMemo(
    () => pageItems.flatMap(({ item }) => (isArticle(item) ? [item] : [])),
    [pageItems]
  );
  const hydration = usePreferredProducts(pageArticles, articleProductType);

  const run = React.useCallback(
    async (write: () => Promise<unknown>, successMessage: string) => {
      try {
        await write();
      } catch (error) {
        if (error instanceof Error)
          enqueueSnackbar(error.message, { variant: "error" });
        // A failed write leaves the list on the order PIM holds, not on a guess.
        await list.refetch().catch(() => undefined);
        return false;
      }
      enqueueSnackbar(successMessage, { variant: "success" });
      return true;
    },
    [list]
  );

  const move = React.useCallback(
    (ranked: Positioned, preferenceMove: PreferenceMove) => {
      const targetIndex = moveTargetIndex(
        preferenceMove,
        ranked.index,
        ranked.groupSize
      );
      if (targetIndex === null) return;
      const group = positioned
        .filter(other => other.group === ranked.group)
        .map(other => other.item);
      const { item } = ranked;
      const moved = t("Moved.", { ns: "Product" });

      if (isArticle(item)) {
        if (!("productType" in target)) return;
        const head = buildMoveHead(
          group.flatMap(other =>
            isArticle(other)
              ? [
                  {
                    supplierId: other.articleNumberIdentifier.supplierId,
                    articleNumber: other.articleNumberIdentifier.articleNumber,
                  },
                ]
              : []
          ),
          ranked.index,
          targetIndex
        );
        return run(
          () =>
            list.reorderProductPreferences({
              variables: {
                input: {
                  scope: item.scope,
                  productType: target.productType,
                  articleNumberIdentifiers: head,
                },
              },
            }),
          moved
        );
      }
      const head = buildMoveHead(
        group.flatMap(other => (isArticle(other) ? [] : [other.brand.id])),
        ranked.index,
        targetIndex
      );
      return run(
        () =>
          list.reorderBrandPreferences({
            variables: {
              input: { scope: item.scope, brandIds: head, ...target },
            },
          }),
        moved
      );
    },
    [positioned, target, list, run, t]
  );

  const setBrandLines = React.useCallback(
    (item: BrandItem, brandLineIds: string[]) =>
      run(
        () =>
          brandPreferences.setBrandPreference({
            variables: {
              input: {
                scope: item.scope,
                brandId: item.brand.id,
                brandLineIds,
                ...target,
              },
            },
          }),
        t("Preference saved.", { ns: "Product" })
      ),
    [brandPreferences, target, run, t]
  );

  const removeBrand = React.useCallback(
    (item: BrandItem) =>
      run(
        () =>
          brandPreferences.deleteBrandPreferences({
            variables: {
              input: {
                scope: item.scope,
                brandIds: [item.brand.id],
                ...target,
              },
            },
          }),
        t("Preference removed.", { ns: "Product" })
      ),
    [brandPreferences, target, run, t]
  );

  const brandScopes = React.useMemo(() => {
    const byBrand = new Map<string, Set<PimProductPreferenceScope>>();
    for (const item of list.items) {
      if (isArticle(item)) continue;
      byBrand.set(
        item.brand.id,
        new Set([...(byBrand.get(item.brand.id) ?? []), item.scope])
      );
    }
    return byBrand;
  }, [list.items]);

  const addBrands = React.useCallback(
    async (
      picked: BrandPreferenceOption[],
      writtenScope: PimProductPreferenceScope
    ) => {
      // An existing entry keeps its lines and its position, so it is not written again.
      const toWrite = picked.filter(
        option => !brandScopes.get(option.id)?.has(writtenScope)
      );
      if (toWrite.length === 0) return;
      const written = await run(
        async () => {
          for (const option of toWrite) {
            await brandPreferences.setBrandPreference({
              variables: {
                input: {
                  scope: writtenScope,
                  brandId: option.id,
                  brandLineIds: [],
                  ...target,
                },
              },
            });
          }
        },
        t("Marked as preferred.", { ns: "Product" })
      );
      // The new brands are where the user should now look.
      if (written) {
        tabs.onChange("BRANDS");
        scope.onChange(writtenScope);
      }
    },
    [brandScopes, brandPreferences, target, run, t, tabs, scope]
  );

  const [picked, setPicked] = React.useState<BrandPreferenceOption[] | null>(
    null
  );

  const writing = list.reordering || brandPreferences.writing;

  const renderBrandActions = (ranked: PositionedPreferenceItem<BrandItem>) => {
    const { item } = ranked;
    const deleted = Boolean(item.brand.deletedAt);
    return (
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        {inert || deleted ? null : (
          <PreferenceMoveButton
            name={item.brand.title}
            index={ranked.index}
            groupSize={ranked.groupSize}
            mayWrite={mayWrite(item.scope)}
            disabled={writing}
            onMove={preferenceMove => move(ranked, preferenceMove)}
          />
        )}
        <BrandPreferenceItemActions
          brand={item.brand}
          lines={item.brandLines.map(({ brandLine }) => brandLine)}
          linesAvailableIn={inert || deleted ? null : brandLinesAvailableIn}
          mayWrite={mayWrite(item.scope)}
          disabled={writing}
          onSaveLines={ids => setBrandLines(item, ids)}
          onRemove={() => removeBrand(item)}
        />
      </Stack>
    );
  };

  const ArticleActions = React.useCallback(
    ({ product }: { product: RankedArticleProduct }) => (
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        {inert ? null : (
          <PreferenceMoveButton
            name={product.texts?.title ?? product.articleNumber}
            index={product.ranked.index}
            groupSize={product.ranked.groupSize}
            mayWrite={mayWrite(product.ranked.item.scope)}
            disabled={writing}
            onMove={preferenceMove => move(product.ranked, preferenceMove)}
          />
        )}
        {renderArticleAction(product, product.ranked.item)}
      </Stack>
    ),
    [inert, mayWrite, writing, move, renderArticleAction]
  );

  const articleProductOf = (
    ranked: PositionedPreferenceItem<ArticleItem>
  ): RankedArticleProduct => ({
    ...(hydration.productOf(ranked.item.articleNumberIdentifier) ??
      toUnresolvedProductSearchResult(ranked.item.articleNumberIdentifier)),
    ranked,
  });

  const header = (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "center", justifyContent: "space-between" }}
    >
      {title}
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <SwitchCollectionViewButton
          allowedViews={ALLOWED_VIEWS}
          activeView={activeView}
          setActiveView={setActiveView}
        />
        {search !== null ? (
          <PreferenceSearchButton productTypeId={search.productTypeId} />
        ) : null}
        <AddPreferenceButton
          onAssignProducts={onAssignProducts}
          brandPicker={
            brandAvailableIn !== null
              ? {
                  title: t("Add brands", { ns: "Product" }),
                  availableIn: brandAvailableIn,
                  useAvailableOptions: useAvailableBrandOptions,
                  emptyLabel: t("No brand is available in the catalogue.", {
                    ns: "Product",
                  }),
                  preferredLabelOf: id => {
                    const scopes = brandScopes.get(id);
                    return scopes ? scopesLabel(t, scopes) : null;
                  },
                  handleComplete: setPicked,
                }
              : null
          }
          disabled={writing}
        />
      </Stack>
      {picked !== null ? (
        <PreferenceScopeModal
          picked={picked}
          handleClose={() => setPicked(null)}
          onChoose={scope => addBrands(picked, scope)}
        />
      ) : null}
    </Stack>
  );

  const viewBar = (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        rowGap: 1,
      }}
    >
      <Tabs
        useSelectOnMobile
        options={tabs.kinds.map(kind => ({
          value: kind,
          label:
            kind === "ARTICLES"
              ? t("Products", { ns: "Product" })
              : t("Brands", { ns: "Product" }),
          count: kindCounts[kind],
        }))}
        value={tabs.kind}
        onChange={tabs.onChange}
      />
      <PreferenceScopeToggle
        value={scope.value}
        onChange={scope.onChange}
        counts={scopeCounts}
      />
    </Stack>
  );

  if (list.error)
    return (
      <Stack direction="column" spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {header}
        {notice}
        {viewBar}
        <ErrorMessage message={list.error.message} />
      </Stack>
    );

  const body =
    list.loading || hydration.loading ? (
      <LoadingSpinner />
    ) : positioned.length === 0 ? (
      <EmptyScopeState
        otherScope={shownScope === "USER" ? "ORGANISATION" : "USER"}
        otherScopeCount={
          scopeCounts[shownScope === "USER" ? "ORGANISATION" : "USER"]
        }
        onShowOtherScope={scope.onChange}
        appliedGlobalBrandCount={
          // A product type without brand entries at either scope ranks by the global ones.
          !("global" in target) &&
          shownKind === "BRANDS" &&
          kindCounts.BRANDS === 0
            ? globalBrandCount
            : 0
        }
      />
    ) : activeView === "table" ? (
      <PreferenceTable
        pageItems={pageItems}
        totalCount={positioned.length}
        paginationModel={clampedPaginationModel}
        setPaginationModel={setPaginationModel}
        articleProductOf={articleProductOf}
        ArticleActions={ArticleActions}
        renderBrandActions={renderBrandActions}
      />
    ) : (
      <PreferenceCollection
        view={activeView === "gallery" ? "gallery" : "list"}
        pageItems={pageItems}
        totalCount={positioned.length}
        paginationModel={clampedPaginationModel}
        setPaginationModel={setPaginationModel}
        articleProductOf={articleProductOf}
        ArticleActions={ArticleActions}
        renderBrandActions={renderBrandActions}
      />
    );

  return (
    <Stack direction="column" spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      {header}
      {notice}
      {viewBar}
      {body}
    </Stack>
  );
}

/** An empty view that points at the other scope's preferences of the same tab, if any. */
function EmptyScopeState({
  otherScope,
  otherScopeCount,
  onShowOtherScope,
  appliedGlobalBrandCount,
}: {
  otherScope: PimProductPreferenceScope;
  otherScopeCount: number;
  onShowOtherScope: (scope: PimProductPreferenceScope) => void;
  /** Global brand entries this product type ranks by, for want of its own; 0 for none. */
  appliedGlobalBrandCount: number;
}) {
  const { t } = useTranslate(["Product"]);

  return (
    <Stack direction="column" spacing={1} sx={{ p: 2, alignItems: "center" }}>
      <Typography variant="body2">
        {t("Nothing is preferred here yet.", { ns: "Product" })}
      </Typography>
      {otherScopeCount > 0 ? (
        <Button size="small" onClick={() => onShowOtherScope(otherScope)}>
          {otherScope === "ORGANISATION"
            ? t("Show your organisation's preferences ({count})", {
                ns: "Product",
                count: otherScopeCount,
              })
            : t("Show my preferences ({count})", {
                ns: "Product",
                count: otherScopeCount,
              })}
        </Button>
      ) : null}
      {appliedGlobalBrandCount > 0 ? (
        <>
          <Typography variant="body2" sx={{ color: "grey.600" }}>
            {t(
              "Without brand preferences of its own, this product type ranks by the global brand preferences ({count}).",
              { ns: "Product", count: appliedGlobalBrandCount }
            )}
          </Typography>
          <Button
            size="small"
            component={RouterLink}
            to={GLOBAL_PRODUCT_PREFERENCES_PATH}
          >
            {t("Show global preferences", { ns: "Product" })}
          </Button>
        </>
      ) : null}
    </Stack>
  );
}

interface ViewProps {
  pageItems: Positioned[];
  totalCount: number;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: (model: { page: number; pageSize: number }) => void;
  articleProductOf: (
    ranked: PositionedPreferenceItem<ArticleItem>
  ) => RankedArticleProduct;
  ArticleActions: React.ComponentType<{ product: RankedArticleProduct }>;
  renderBrandActions: (
    ranked: PositionedPreferenceItem<BrandItem>
  ) => React.ReactNode;
}

/** The list and gallery views: a heading wherever a group starts on the page. */
function PreferenceCollection({
  view,
  pageItems,
  totalCount,
  paginationModel,
  setPaginationModel,
  articleProductOf,
  ArticleActions,
  renderBrandActions,
}: ViewProps & { view: "list" | "gallery" }) {
  const { t } = useTranslate(["Product", "Global"]);
  const getProductTo = useGetProductTo();
  const { spacing, sizeMeasureRef, columns } = useCardsNumber();

  const sections = pageItems.reduce<
    { group: PreferenceGroup; items: Positioned[] }[]
  >((result, ranked) => {
    const last = result.at(-1);
    return last && last.group === ranked.group
      ? [...result.slice(0, -1), { ...last, items: [...last.items, ranked] }]
      : [...result, { group: ranked.group, items: [ranked] }];
  }, []);

  const renderItem = (ranked: Positioned) => {
    if (isArticleRanked(ranked)) {
      const product = articleProductOf(ranked);
      const tags = (
        <PreferenceItemTags
          position={ranked.index + 1}
          notRanking={ranked.item.beyondRankDepth}
          deleted={false}
        />
      );
      return view === "gallery" ? (
        <ProductCardItem
          product={product}
          getProductTo={getProductTo}
          ActionButton={ArticleActions}
          Tags={tags}
        />
      ) : (
        <ProductListItem
          product={product}
          getProductTo={getProductTo}
          ActionButton={ArticleActions}
          Tags={tags}
        />
      );
    }
    if (!isBrandRanked(ranked)) return null;
    const itemProps = {
      title: ranked.item.brand.title,
      Info: <BrandLines item={ranked.item} />,
      Tags: (
        <PreferenceItemTags
          position={ranked.index + 1}
          notRanking={ranked.item.beyondRankDepth}
          deleted={Boolean(ranked.item.brand.deletedAt)}
        />
      ),
      ActionButton: renderBrandActions(ranked),
    };
    return view === "gallery" ? (
      <MediaCardItem {...itemProps} />
    ) : (
      <MediaListItem {...itemProps} />
    );
  };

  return (
    <Stack
      ref={sizeMeasureRef}
      direction="column"
      spacing={1}
      sx={{ flex: 1, overflow: "auto", minHeight: 0 }}
    >
      <Stack
        direction="column"
        spacing={2}
        sx={{ overflow: "auto", flex: 1, minHeight: 0 }}
      >
        {sections.map(section => (
          <Stack
            key={section.group}
            component="section"
            direction="column"
            spacing={1}
            aria-label={groupLabel(t, section.group)}
          >
            {view === "gallery" ? (
              <Grid container spacing={spacing}>
                {section.items.map(ranked => (
                  <Grid key={itemKey(ranked.item)} size={columns}>
                    {renderItem(ranked)}
                  </Grid>
                ))}
              </Grid>
            ) : (
              section.items.map(ranked => (
                <React.Fragment key={itemKey(ranked.item)}>
                  {renderItem(ranked)}
                </React.Fragment>
              ))
            )}
          </Stack>
        ))}
      </Stack>
      <TablePagination
        component="div"
        count={totalCount}
        page={paginationModel.page}
        onPageChange={(_, newPage) =>
          setPaginationModel({
            pageSize: paginationModel.pageSize,
            page: newPage,
          })
        }
        rowsPerPage={paginationModel.pageSize}
        onRowsPerPageChange={event =>
          setPaginationModel({
            pageSize: parseInt(event.target.value, 10),
            page: 0,
          })
        }
        rowsPerPageOptions={RESULTS_PER_PAGE_OPTIONS}
        labelRowsPerPage={t("Per page:", { ns: "Global" })}
      />
    </Stack>
  );
}

type TableRow = { id: string; ranked: Positioned };

/** The table view of one tab and scope. */
function PreferenceTable({
  pageItems,
  totalCount,
  paginationModel,
  setPaginationModel,
  articleProductOf,
  ArticleActions,
  renderBrandActions,
}: ViewProps) {
  const { t } = useTranslate(["Product", "Global"]);
  const stateStore = useDataGridStateStore("RankedPreferenceList");

  const rows = pageItems.map(ranked => ({ id: itemKey(ranked.item), ranked }));

  const columns: GridColDef<TableRow>[] = [
    {
      field: "position",
      headerName: "#",
      width: 60,
      sortable: false,
      renderCell: ({ row }) => row.ranked.index + 1,
    },
    {
      field: "name",
      headerName: t("Preference", { ns: "Product" }),
      flex: 3,
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) =>
        isArticleRanked(row.ranked)
          ? (articleProductOf(row.ranked).texts?.title ??
            row.ranked.item.articleNumberIdentifier.articleNumber)
          : isBrandRanked(row.ranked)
            ? row.ranked.item.brand.title
            : null,
    },
    {
      field: "details",
      headerName: t("Details", { ns: "Product" }),
      flex: 3,
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) =>
        isArticleRanked(row.ranked) ? (
          row.ranked.item.articleNumberIdentifier.articleNumber
        ) : isBrandRanked(row.ranked) ? (
          <BrandLines item={row.ranked.item} />
        ) : null,
    },
    {
      field: "status",
      headerName: t("Status", { ns: "Global" }),
      flex: 2,
      minWidth: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <PreferenceItemTags
          position={row.ranked.index + 1}
          notRanking={row.ranked.item.beyondRankDepth}
          deleted={
            isBrandRanked(row.ranked) &&
            Boolean(row.ranked.item.brand.deletedAt)
          }
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      resizable: false,
      align: "right",
      renderCell: ({ row }) =>
        isArticleRanked(row.ranked) ? (
          <ArticleActions product={articleProductOf(row.ranked)} />
        ) : isBrandRanked(row.ranked) ? (
          renderBrandActions(row.ranked)
        ) : null,
    },
  ];

  return (
    <DataGrid
      stateStore={stateStore}
      density="compact"
      columns={columns}
      rows={rows}
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
      rowCount={totalCount}
      hideFooter={totalCount === 0}
    />
  );
}

/** A brand item's lines in order, at most three named, every one in the tooltip. */
function BrandLines({ item }: { item: BrandItem }) {
  const { t } = useTranslate(["Product"]);
  const summary = brandLineSummary(
    item.brandLines.map(({ brandLine }) => ({ title: lineName(brandLine) }))
  );

  if (summary.kind === "wholeBrand")
    return (
      <Typography variant="body2" sx={{ color: "grey.600" }}>
        {t("Whole brand", { ns: "Product" })}
      </Typography>
    );

  return (
    <Tooltip
      title={
        <>
          {item.brandLines.map(({ brandLine, beyondRankDepth }) => (
            <div key={brandLine.id}>
              {lineName(brandLine)}
              {brandLine.deletedAt
                ? ` (${t("Deleted in the catalogue", { ns: "Product" })})`
                : ""}
              {beyondRankDepth
                ? ` (${t("Does not rank", { ns: "Product" })})`
                : ""}
            </div>
          ))}
        </>
      }
    >
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
        {item.brandLines.slice(0, summary.shown.length).map(line => (
          <Stack
            key={line.brandLine.id}
            direction="row"
            spacing={0.5}
            sx={{ alignItems: "center" }}
          >
            <Chip
              size="small"
              label={lineName(line.brandLine)}
              color={line.brandLine.deletedAt ? "warning" : "default"}
              sx={line.beyondRankDepth ? { color: "grey.600" } : undefined}
            />
            {line.brandLine.deletedAt ? (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={t("Deleted in the catalogue", { ns: "Product" })}
              />
            ) : null}
            {line.beyondRankDepth ? <NotRankingChip /> : null}
          </Stack>
        ))}
        {summary.moreCount > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            label={t("+{count} more", {
              ns: "Product",
              count: summary.moreCount,
            })}
          />
        ) : null}
      </Stack>
    </Tooltip>
  );
}

function isArticle(item: PreferenceListItemFragment): item is ArticleItem {
  return item.__typename === "PimArticlePreferenceItem";
}

function isArticleRanked(
  ranked: Positioned
): ranked is PositionedPreferenceItem<ArticleItem> {
  return isArticle(ranked.item);
}

function isBrandRanked(
  ranked: Positioned
): ranked is PositionedPreferenceItem<BrandItem> {
  return ranked.item.__typename === "PimBrandPreferenceItem";
}

function itemKey(item: PreferenceListItemFragment): string {
  return isArticle(item)
    ? `${item.scope}/article/${articleNumberIdentifierKey(item.articleNumberIdentifier)}`
    : `${item.scope}/brand/${item.brand.id}`;
}

type Translate = ReturnType<typeof useTranslate>["t"];

function groupLabel(t: Translate, group: PreferenceGroup): string {
  switch (group) {
    case "USER_ARTICLES":
      return t("Your articles", { ns: "Product" });
    case "ORGANISATION_ARTICLES":
      return t("Your organisation's articles", { ns: "Product" });
    case "USER_BRANDS":
      return t("Your brands", { ns: "Product" });
    case "ORGANISATION_BRANDS":
      return t("Your organisation's brands", { ns: "Product" });
  }
}

function scopesLabel(
  t: Translate,
  scopes: ReadonlySet<PimProductPreferenceScope>
): string {
  return scopes.has("USER") && scopes.has("ORGANISATION")
    ? t("Preferred by you and your organisation", { ns: "Product" })
    : scopes.has("USER")
      ? t("Preferred by you", { ns: "Product" })
      : t("Preferred by your organisation", { ns: "Product" });
}
